import React, { useState, useCallback, useContext } from 'react';
import styled from 'styled-components';
import {
  Input,
  Select as AntdSelect,
  AutoComplete,
  Typography,
  Table,
  Space,
  Divider,
  Button,
  Modal,
} from 'antd';
import { AntdSelect as Select, ApplicationUIContext, Switch, useModalState } from 'ui-library';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';
import DatePicker from 'components/ui/DatePicker';
import Form from 'components/ui/Form';
import Spacer from 'components/ui/Spacer';
import CheckboxNormal from 'components/ui/CheckboxNormal';
import moment from 'moment';
import { getTranslatedString, getCurrentLocale } from 'helpers/i18n';
import { Formik, yupToFormErrors } from 'formik';
import * as Yup from 'yup';
import { EditTwoTone, DeleteTwoTone } from '@ant-design/icons';
import { IncentivesContext } from 'components/landlord_app/payments/LandlordIncentives';
import Associations from './Associations';
import NumberFormat from 'react-number-format';

const { Title } = Typography;
const { TextArea } = Input;
const MAX_PERCENTAGE_VALUE = 100;

const FullWidthSpace = styled(Space)`
  width: 100%;
`;

const HalfColumnSpace = styled(Space)`
  width: 100%;
  > * {
    width: calc(50% - 8px);
  }
`;

const StyledSpaceWithEquallySizedItems = styled(Space)`
  width: 100%;

  > * {
    flex: 1;
  }
`;

const FullWidthDatePicker = styled(DatePicker)`
  width: 100%;
`;

const ScheduledEventFormContainer = styled.div`
  border: 1px solid #d9d9d9;
  background: #fafafa;
  padding: 16px;
`;

const InternalName = ({ values, errors, touched, setFieldValue }) => {
  return (
    <Form.Item
      label={getTranslatedString('landlordPortal.incentives.internalName')}
      help={touched.internal_name && errors.internal_name}
      validateStatus={touched.internal_name && errors.internal_name && 'error'}
      hasFeedback
      required
    >
      <Input
        value={values.internal_name}
        error={touched.internal_name && errors.internal_name}
        onChange={(event) => setFieldValue('internal_name', event.target.value)}
      />
    </Form.Item>
  );
};

const InternalDescription = ({ values, errors, touched, setFieldValue }) => {
  return (
    <Form.Item
      label={getTranslatedString('landlordPortal.incentives.internalDescription')}
      help={touched.internal_description && errors.internal_description}
      validateStatus={touched.internal_description && errors.internal_description && 'error'}
      hasFeedback
      required
    >
      <TextArea
        value={values.internal_description}
        error={touched.internal_description && errors.internal_description}
        onChange={(event) => setFieldValue('internal_description', event.target.value)}
      />
    </Form.Item>
  );
};

const ExternallyVisible = ({ values, errors, touched, setFieldValue, isMobile }) => {
  return (
    <Form.Item help={touched.externally_visible && errors.externally_visible}>
      <Switch
        label={getTranslatedString('landlordPortal.incentives.externallyVisible')}
        checked={!!values.externally_visible}
        value={values.externally_visible}
        name="externally_visible"
        onChange={(val) => setFieldValue('externally_visible', val)}
      />
    </Form.Item>
  );
};

const ExternalName = ({ values, errors, touched, setFieldValue, isMobile }) => {
  return (
    <Form.Item
      label={getTranslatedString('landlordPortal.incentives.externalName')}
      help={touched.external_name && errors.external_name}
      validateStatus={touched.external_name && errors.external_name && 'error'}
      hasFeedback
      required
    >
      <Input
        value={values.external_name}
        error={touched.external_name && errors.external_name}
        onChange={(event) => setFieldValue('external_name', event.target.value)}
        isMobile={isMobile}
      />
    </Form.Item>
  );
};

const ExternalDescription = ({ values, errors, touched, setFieldValue, isMobile }) => {
  return (
    <Form.Item
      label={getTranslatedString('landlordPortal.incentives.externalDescription')}
      help={touched.external_description && errors.external_description}
      validateStatus={touched.external_description && errors.external_description && 'error'}
      hasFeedback
      required
    >
      <TextArea
        value={values.external_description}
        error={touched.external_description && errors.external_description}
        onChange={(event) => setFieldValue('external_description', event.target.value)}
      />
    </Form.Item>
  );
};

const IncentiveName = ({ values, errors, touched, setFieldValue }) => {
  return (
    <Form.Item
      label={getTranslatedString('landlordPortal.incentives.incentiveName')}
      help={touched.name && errors.name}
      validateStatus={touched.name && errors.name && 'error'}
      hasFeedback
      required
    >
      <Input
        value={values.name}
        error={touched.name && errors.name}
        onChange={(event) => setFieldValue('name', event.target.value)}
      />
    </Form.Item>
  );
};

const ValidDates = ({ values, errors, touched, setFieldValue, isMobile }) => {
  const exampleYear = new Date().getFullYear();
  const exampleFrom = `${exampleYear}-01-01`;
  const exampleTo = `${exampleYear + 1}-01-01`;

  return (
    <HalfColumnSpace direction={isMobile ? 'vertical' : 'horizontal'} align="start">
      <Form.Item
        label={getTranslatedString('landlordPortal.incentives.validFrom')}
        validateStatus={touched.valid_from && errors.valid_from && 'error'}
        help={touched.valid_from && errors.valid_from}
        hasFeedback
        required
      >
        <FullWidthDatePicker
          name="valid_from"
          defaultPickerValue={moment().locale(getCurrentLocale())}
          showToday={false}
          value={values.valid_from}
          onChange={(value) => setFieldValue('valid_from', value, true)}
          minDate={new Date()}
          mobileMode="date"
          placeholder={exampleFrom}
          id="error"
        />
      </Form.Item>
      <Form.Item
        label={getTranslatedString('landlordPortal.incentives.validTo')}
        validateStatus={touched.valid_to && errors.valid_to && 'error'}
        help={touched.valid_to && errors.valid_to}
        hasFeedback
        required={!values.wont_expire}
      >
        <FullWidthDatePicker
          name="valid_to"
          defaultPickerValue={moment().locale(getCurrentLocale())}
          showToday={false}
          value={values.valid_to}
          onChange={(value) => setFieldValue('valid_to', value, true)}
          minDate={values.valid_from ? values.valid_from : new Date()}
          mobileMode="date"
          placeholder={exampleTo}
          disabled={values.wont_expire}
          id="error"
        />
      </Form.Item>
    </HalfColumnSpace>
  );
};

const DoesNotExpire = ({ values, errors, touched, setFieldValue, isMobile }) => {
  return (
    <CheckboxNormal
      name="wont_expire"
      value={values.wont_expire}
      checked={values.wont_expire}
      onChange={(e) => setFieldValue('wont_expire', e.target.checked)}
      style={{ marginLeft: 0, marginRight: 0 }}
    >
      {getTranslatedString('landlordPortal.incentives.doesNotExpire')}
    </CheckboxNormal>
  );
};

const ApprovalType = ({ values, errors, touched, setFieldValue, isMobile }) => {
  const approvalTypeOptions = [
    { value: 'automatic', label: getTranslatedString('landlordPortal.incentives.automatic') },
    { value: 'manual', label: getTranslatedString('landlordPortal.incentives.manual') },
  ];

  return (
    <Form.Item
      label={getTranslatedString('landlordPortal.incentives.approvalType')}
      help={touched.approval_type && errors.approval_type}
      hasFeedback
      required
    >
      <Select
        optionData={approvalTypeOptions}
        value={values.approval_type}
        error={errors.approval_type && touched.approval_type}
        handleChange={(value) => setFieldValue('approval_type', value)}
        isMobile={isMobile}
      />
    </Form.Item>
  );
};

const Inventory = ({ values, errors, touched, setFieldValue, isMobile }) => {
  const options = [
    { value: 'weekly', label: getTranslatedString('landlordPortal.incentives.weekly') },
    { value: 'monthly', label: getTranslatedString('landlordPortal.incentives.monthly') },
  ];

  return (
    <HalfColumnSpace direction={isMobile ? 'vertical' : 'horizontal'} align="start">
      <Form.Item
        label={getTranslatedString('landlordPortal.incentives.inventory')}
        help={touched.inventory && errors.inventory}
        validateStatus={touched.inventory && errors.inventory && 'error'}
      >
        <NumberFormat
          allowNegative={false}
          fixedDecimalScale={true}
          decimalScale={0}
          customInput={Input}
          format="#####"
          value={values.inventory}
          error={touched.inventory && errors.inventory}
          onChange={(event) => setFieldValue('inventory', event.target.value)}
        />
      </Form.Item>
      <Form.Item
        label={getTranslatedString('landlordPortal.incentives.inventoryReset')}
        help={touched.inventory_reset_frequency && errors.inventory_reset_frequency}
      >
        <Select
          optionData={options}
          value={values.inventory_reset_frequency}
          error={errors.inventory_reset_frequency && touched.inventory_reset_frequency}
          handleChange={(value) => setFieldValue('inventory_reset_frequency', value)}
          isMobile={isMobile}
        />
      </Form.Item>
    </HalfColumnSpace>
  );
};

const AutoApprovedInventory = ({ values, errors, touched, setFieldValue, isMobile }) => {
  const options = [
    { value: 'weekly', label: getTranslatedString('landlordPortal.incentives.weekly') },
    { value: 'monthly', label: getTranslatedString('landlordPortal.incentives.monthly') },
  ];

  return (
    <HalfColumnSpace direction={isMobile ? 'vertical' : 'horizontal'} align="start">
      <Form.Item
        label={getTranslatedString('landlordPortal.incentives.autoApprovedInventory')}
        help={touched.auto_approved_inventory && errors.auto_approved_inventory}
        validateStatus={
          touched.auto_approved_inventory && errors.auto_approved_inventory && 'error'
        }
      >
        <NumberFormat
          allowNegative={false}
          fixedDecimalScale={true}
          decimalScale={0}
          customInput={Input}
          format="#####"
          value={values.auto_approved_inventory}
          error={touched.auto_approved_inventory && errors.auto_approved_inventory}
          onChange={(event) => setFieldValue('auto_approved_inventory', event.target.value)}
        />
      </Form.Item>
      <Form.Item
        label={getTranslatedString('landlordPortal.incentives.autoApprovedInventoryReset')}
        help={
          touched.auto_approved_inventory_reset_frequency &&
          errors.auto_approved_inventory_reset_frequency
        }
      >
        <Select
          optionData={options}
          value={values.auto_approved_inventory_reset_frequency}
          error={
            errors.auto_approved_inventory_reset_frequency &&
            touched.auto_approved_inventory_reset_frequency
          }
          handleChange={(value) => setFieldValue('auto_approved_inventory_reset_frequency', value)}
          isMobile={isMobile}
        />
      </Form.Item>
    </HalfColumnSpace>
  );
};

const IncentiveCodes = ({ values, errors, touched, setFieldValue }) => {
  const options = [{ value: 'CODE1234' }, { value: 'CODE4567' }];
  const value = values.incentive_codes ? values.incentive_codes.split(',') : [];

  return (
    <Form.Item
      label={getTranslatedString('landlordPortal.incentives.incentiveCodes')}
      help={touched.incentive_codes && errors.incentive_codes}
      validateStatus={touched.incentive_codes && errors.incentive_codes && 'error'}
      hasFeedback
      required
    >
      <AntdSelect
        value={value}
        error={touched.incentive_codes && errors.incentive_codes}
        mode="tags"
        onChange={(value) => setFieldValue('incentive_codes', value.join(','))}
      >
        {options.map((option) => (
          <AntdSelect.Option key={option.value} value={option.value}>
            {option.value}
          </AntdSelect.Option>
        ))}
      </AntdSelect>
    </Form.Item>
  );
};

const InternalCode = ({ values, errors, touched, setFieldValue }) => {
  const options = [{ value: 'CODE1234' }, { value: 'CODE4567' }];

  return (
    <Form.Item
      label={getTranslatedString('landlordPortal.incentives.internalCode')}
      help={touched.internal_code && errors.internal_code}
      validateStatus={touched.internal_code && errors.internal_code && 'error'}
      hasFeedback
      required
    >
      <AutoComplete
        value={values.internal_code}
        onChange={(value) => setFieldValue('internal_code', value)}
        error={touched.internal_code && errors.internal_code}
        options={options}
      />
    </Form.Item>
  );
};

const ScheduleAndCalculationType = ({ values, errors, touched, setFieldValue, isMobile }) => {
  return (
    <HalfColumnSpace size="middle" wrap>
      <Form.Item
        label={getTranslatedString('landlordPortal.incentives.scheduleType')}
        help={touched.schedule_type && errors.schedule_type}
        hasFeedback
        required
      >
        <Select
          optionData={[
            { value: 'monthly', label: getTranslatedString('landlordPortal.incentives.monthly') },
            { value: 'custom', label: getTranslatedString('landlordPortal.incentives.custom') },
          ]}
          value={values.schedule_type}
          error={errors.schedule_type && touched.schedule_type}
          handleChange={(value) => setFieldValue('schedule_type', value)}
          isMobile={isMobile}
        />
      </Form.Item>
      <Form.Item
        label={getTranslatedString('landlordPortal.incentives.calculationType')}
        help={touched.calculation_type && errors.calculation_type}
        hasFeedback
        required
      >
        <Select
          optionData={[
            {
              value: 'percentage',
              label: getTranslatedString('landlordPortal.incentives.percentage'),
            },
            {
              value: 'absolute',
              label: getTranslatedString('landlordPortal.incentives.absolute'),
            },
          ]}
          value={values.calculation_type}
          error={errors.calculation_type && touched.calculation_type}
          handleChange={(value) => setFieldValue('calculation_type', value)}
          isMobile={isMobile}
        />
      </Form.Item>
    </HalfColumnSpace>
  );
};

const MonthlyScheduleFields = ({ values, errors, touched, setFieldValue, isMobile }) => {
  const startingMonthOptions = new Array(18).fill(null).map((_, i) => ({
    value: i + 1,
    label: getTranslatedString('landlordPortal.incentives.monthNOfLease', { n: i + 1 }),
  }));

  return (
    <HalfColumnSpace size="middle" wrap>
      <Form.Item
        label={getTranslatedString('landlordPortal.incentives.amountPerMonth')}
        help={touched.schedule_monthly_amount && errors.schedule_monthly_amount}
        validateStatus={
          touched.schedule_monthly_amount && errors.schedule_monthly_amount && 'error'
        }
      >
        <NumberFormat
          value={values.schedule_monthly_amount}
          allowNegative={false}
          decimalScale={2}
          fixedDecimalScale={true}
          customInput={Input}
          format={values.calculation_type === 'percentage' ? '###' : '######'}
          isAllowed={({ floatValue }) =>
            values.calculation_type === 'percentage' ? floatValue <= MAX_PERCENTAGE_VALUE : true
          }
          addonAfter={values.calculation_type === 'percentage' ? '%' : null}
          addonBefore={values.calculation_type === 'absolute' ? '$' : null}
          error={touched.schedule_monthly_amount && errors.schedule_monthly_amount}
          onChange={(event) => setFieldValue('schedule_monthly_amount', event.target.value)}
        />
      </Form.Item>
      <Form.Item
        label={getTranslatedString('landlordPortal.incentives.durationInMonths')}
        help={touched.schedule_monthly_duration_months && errors.schedule_monthly_duration_months}
        validateStatus={
          touched.schedule_monthly_duration_months &&
          errors.schedule_monthly_duration_months &&
          'error'
        }
      >
        <NumberFormat
          allowNegative={false}
          fixedDecimalScale={true}
          decimalScale={0}
          customInput={Input}
          format="##"
          value={values.schedule_monthly_duration_months}
          error={
            touched.schedule_monthly_duration_months && errors.schedule_monthly_duration_months
          }
          onChange={(event) =>
            setFieldValue('schedule_monthly_duration_months', event.target.value)
          }
        />
      </Form.Item>
      <Form.Item
        label={getTranslatedString('landlordPortal.incentives.starting')}
        help={touched.schedule_monthly_starting_month && errors.schedule_monthly_starting_month}
        hasFeedback
        required
      >
        <Select
          optionData={startingMonthOptions}
          value={values.schedule_monthly_starting_month}
          error={errors.schedule_monthly_starting_month && touched.schedule_monthly_starting_month}
          handleChange={(value) => setFieldValue('schedule_monthly_starting_month', value)}
          isMobile={isMobile}
        />
      </Form.Item>
    </HalfColumnSpace>
  );
};

const ScheduledEventForm = ({ events, selectedIndex, handleFormSubmit, onClose }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const formData = selectedIndex === -1 ? {} : events[selectedIndex];
  const validationSchema = {
    month: Yup.number()
      .required(getTranslatedString('required'))
      .test('month', getTranslatedString('landlordPortal.incentives.alreadyExist'), (value) => {
        return (
          events.filter((e, i) => i !== selectedIndex).findIndex((e) => e.month === value) === -1
        );
      }),
    amount: Yup.number().required(getTranslatedString('required')),
  };

  const validateForm = async (values) => {
    const schema = Yup.object().shape(validationSchema);
    return schema.validate(values, { abortEarly: false }).catch((error) => {
      throw yupToFormErrors(error);
    });
  };

  const initialValues = {
    month: formData.month || 1,
    amount: formData.amount || 10,
    calculation_type: formData.calculation_type || 'percentage',
  };

  const monthOptions = new Array(18).fill(null).map((_, i) => ({ value: i + 1, label: i + 1 }));

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      validate={validateForm}
      enableReinitialize
    >
      {({
        handleSubmit,
        values,
        handleChange,
        handleBlur,
        setValues,
        touched,
        errors,
        setFieldValue,
      }) => {
        return (
          <VerticalFormWrapper onSubmit={handleSubmit}>
            <ScheduledEventFormContainer>
              <Title level={5}>
                {selectedIndex === -1
                  ? getTranslatedString('landlordPortal.incentives.addScheduleEvent')
                  : getTranslatedString('landlordPortal.incentives.updateScheduleEvent')}
              </Title>
              <StyledSpaceWithEquallySizedItems direction={isMobile && 'vertical'}>
                <Form.Item
                  label={getTranslatedString('landlordPortal.incentives.monthOfLease')}
                  help={touched.month && errors.month}
                  validateStatus={touched.month && errors.month && 'error'}
                  hasFeedback
                  required
                >
                  <Select
                    optionData={monthOptions}
                    value={values.month}
                    error={errors.month && touched.month}
                    handleChange={(value) => setFieldValue('month', value)}
                    isMobile={isMobile}
                  />
                </Form.Item>
                <Form.Item
                  label={getTranslatedString('landlordPortal.incentives.calculationType')}
                  help={touched.calculation_type && errors.calculation_type}
                  validateStatus={touched.calculation_type && errors.calculation_type && 'error'}
                  hasFeedback
                  required
                >
                  <Select
                    optionData={[
                      {
                        value: 'percentage',
                        label: getTranslatedString('landlordPortal.incentives.percentage'),
                      },
                      {
                        value: 'absolute',
                        label: getTranslatedString('landlordPortal.incentives.absolute'),
                      },
                    ]}
                    value={values.calculation_type}
                    error={errors.calculation_type && touched.calculation_type}
                    handleChange={(value) => setFieldValue('calculation_type', value)}
                    isMobile={isMobile}
                  />
                </Form.Item>
                <Form.Item
                  label={getTranslatedString('landlordPortal.incentives.amount')}
                  help={touched.amount && errors.amount}
                  validateStatus={touched.amount && errors.amount && 'error'}
                  hasFeedback
                  required
                >
                  <NumberFormat
                    allowNegative={false}
                    decimalScale={2}
                    fixedDecimalScale={true}
                    customInput={Input}
                    format={values.calculation_type === 'percentage' ? '###' : '######'}
                    isAllowed={({ floatValue }) =>
                      values.calculation_type === 'percentage'
                        ? floatValue <= MAX_PERCENTAGE_VALUE
                        : true
                    }
                    value={values.amount}
                    addonAfter={values.calculation_type === 'percentage' ? '%' : null}
                    addonBefore={values.calculation_type === 'absolute' ? '$' : null}
                    error={touched.amount && errors.amount}
                    onChange={(event) => setFieldValue('amount', event.target.value)}
                  />
                </Form.Item>
              </StyledSpaceWithEquallySizedItems>
              <Space style={{ marginTop: 16, display: 'flex' }}>
                <Button htmlType="submit" type="primary">
                  {selectedIndex === -1
                    ? getTranslatedString('landlordPortal.incentives.add')
                    : getTranslatedString('landlordPortal.incentives.update')}
                </Button>
                <Button onClick={onClose}>
                  {getTranslatedString('landlordPortal.incentives.cancel')}
                </Button>
              </Space>
            </ScheduledEventFormContainer>
          </VerticalFormWrapper>
        );
      }}
    </Formik>
  );
};

const CustomScheduleFields = ({ values, errors, touched, setFieldValue, isMobile }) => {
  const [showEventForm, setShowEventForm] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const handleFormSubmit = useCallback(
    (event) => {
      const newScheduleCustomEvents = [...values.schedule_custom_events];
      if (selectedIndex === -1) {
        newScheduleCustomEvents.splice(values.schedule_custom_events.length, 0, event);
      } else {
        newScheduleCustomEvents.splice(selectedIndex, 1, event);
      }

      setFieldValue('schedule_custom_events', newScheduleCustomEvents);
      setShowEventForm(false);
      setSelectedIndex(-1);
    },
    [selectedIndex, values.schedule_custom_events]
  );

  const handleEditClicked = useCallback((event) => {
    setSelectedIndex(event.index);
    setShowEventForm(true);
  }, []);

  const handleDeleteClicked = useCallback(
    (event) => {
      const newScheduleCustomEvents = [...values.schedule_custom_events];
      newScheduleCustomEvents.splice(event.index, 1);
      setFieldValue('schedule_custom_events', newScheduleCustomEvents);
    },
    [values.schedule_custom_events]
  );

  const dataSource = values.schedule_custom_events.map((e, i) => ({ ...e, index: i }));

  return (
    <div>
      {!showEventForm && (
        <Spacer mt={1}>
          <Button onClick={() => setShowEventForm(true)}>
            {getTranslatedString('landlordPortal.incentives.addScheduledEvent')}
          </Button>
          <Form.Item
            validateStatus={
              touched.schedule_custom_events && errors.schedule_custom_events && 'error'
            }
            help={touched.schedule_custom_events && errors.schedule_custom_events}
            hasFeedback
            required
          />
        </Spacer>
      )}
      {showEventForm && (
        <Spacer mt={1}>
          <ScheduledEventForm
            selectedIndex={selectedIndex}
            events={dataSource}
            handleFormSubmit={handleFormSubmit}
            onClose={() => setShowEventForm(false)}
          />
        </Spacer>
      )}
      {dataSource.length > 0 && (
        <Spacer mt={1}>
          <Table
            dataSource={dataSource}
            rowKey={(record) => record.index}
            columns={[
              {
                title: getTranslatedString('landlordPortal.incentives.month'),
                dataType: 'text',
                dataIndex: 'month',
                key: 'month',
              },
              {
                title: getTranslatedString('landlordPortal.incentives.amount'),
                dataType: 'text',
                dataIndex: 'amount',
                key: 'amount',
                render: (_, rowData) => {
                  return (
                    <span>
                      {rowData.amount} {rowData.calculation_type === 'percentage' ? '%' : '$'}
                    </span>
                  );
                },
              },
              {
                title: getTranslatedString('landlordPortal.incentives.actions'),
                dataIndex: 'actions',
                key: 'actions',
                render: (text, rowData) => {
                  return (
                    <Space size="middle">
                      <Button
                        type="link"
                        icon={<EditTwoTone />}
                        onClick={() => handleEditClicked(rowData)}
                      />
                      <Button
                        type="link"
                        icon={<DeleteTwoTone />}
                        onClick={() => handleDeleteClicked(rowData)}
                      />
                    </Space>
                  );
                },
              },
            ]}
            pagination={false}
          />
        </Spacer>
      )}
    </div>
  );
};

const requiredErrorVerbiage = getTranslatedString('required');

const IncentiveFormValidationSchema = Yup.object().shape({
  name: Yup.string().required(requiredErrorVerbiage),
  valid_from: Yup.string().required(requiredErrorVerbiage).ensure(),
  valid_to: Yup.string().when(['wont_expire', 'valid_from'], (wont_expire, valid_from, schema) => {
    if (wont_expire) {
      return schema;
    }

    if (valid_from) {
      return schema
        .required(requiredErrorVerbiage)
        .ensure()
        .test(
          'after-valid-from',
          getTranslatedString('landlordPortal.incentives.validTo.errors.lessThan'),
          (value) => {
            return moment(value, 'YYYY-MM-DD').isSameOrAfter(moment(valid_from, 'YYYY-MM-DD'));
          }
        );
    }

    return schema.required(requiredErrorVerbiage).ensure();
  }),
  wont_expire: Yup.boolean(),
  inventory: Yup.number().required(requiredErrorVerbiage),
  auto_approved_inventory: Yup.number().required(requiredErrorVerbiage),
});

const IncentiveForm = ({ visible, formData, handleFormSubmit, onClose }) => {
  const validateForm = async (values) => {
    return IncentiveFormValidationSchema.validate(values, { abortEarly: false }).catch((error) => {
      throw yupToFormErrors(error);
    });
  };

  const randomId = (new Date() % 9e6).toString(36);

  const initialValues = {
    id: formData.id || `new_${randomId}`,
    name: formData.name || undefined,
    wont_expire: formData.wont_expire || false,
    valid_from: formData.valid_from || undefined,
    valid_to: formData.valid_to || undefined,
    inventory: formData.inventory || undefined,
    inventory_reset_frequency: formData.inventory_reset_frequency || 'monthly',
    auto_approved_inventory: formData.auto_approved_inventory || undefined,
    auto_approved_inventory_reset_frequency:
      formData.auto_apptoved_inventory_reset_frequency || 'monthly',
    unit_ids: formData.unit_ids || [],
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      validate={validateForm}
      enableReinitialize
    >
      {(props) => {
        return (
          <Modal
            width={900}
            visible={visible}
            destroyOnClose
            title={
              String(props.values.id).startsWith('new')
                ? getTranslatedString('landlordPortal.incentives.addIncentive')
                : getTranslatedString('landlordPortal.incentives.editIncentive')
            }
            footer={[
              <Button key="submit" htmlType="submit" type="primary" form="incentive_form">
                {String(props.values.id).startsWith('new')
                  ? getTranslatedString('landlordPortal.incentives.add')
                  : getTranslatedString('landlordPortal.incentives.update')}
              </Button>,
            ]}
            onCancel={onClose}
          >
            <VerticalFormWrapper onSubmit={props.handleSubmit} id="incentive_form">
              <FullWidthSpace direction="vertical" size="middle">
                <Title level={5} style={{ margin: 0 }}>
                  {getTranslatedString('landlordPortal.incentives.details')}
                </Title>
                <IncentiveName {...props} />
                <ValidDates {...props} />
                <DoesNotExpire {...props} />
                <Inventory {...props} />
                <AutoApprovedInventory {...props} />
              </FullWidthSpace>
              <Divider />
              <Associations {...props} />
            </VerticalFormWrapper>
          </Modal>
        );
      }}
    </Formik>
  );
};

const Incentives = ({ values, errors, touched, setFieldValue, isMobile }) => {
  const incentivesData = useContext(IncentivesContext);
  const { currentIncentiveId } = incentivesData;
  const [selectedIndex, setSelectedIndex] = useState(() => {
    if (!currentIncentiveId || !values.incentives) {
      return -1;
    }

    return values.incentives.findIndex((incentive) => incentive.id == currentIncentiveId);
  });
  const formModal = useModalState(selectedIndex > -1);

  const handleFormSubmit = useCallback(
    (incentive) => {
      const newIncentives = [...values.incentives];
      if (selectedIndex === -1) {
        newIncentives.splice(values.incentives.length, 0, incentive);
      } else {
        newIncentives.splice(selectedIndex, 1, incentive);
      }

      setFieldValue('incentives', newIncentives);
      formModal.close();
      setSelectedIndex(-1);
    },
    [selectedIndex, values.incentives]
  );

  const handleEditClicked = useCallback((incentive) => {
    setSelectedIndex(incentive.index);
    formModal.open();
  }, []);

  const handleDeleteClicked = useCallback(
    (incentive) => {
      const newIncentives = [...values.incentives];
      newIncentives.splice(incentive.index, 1);
      setFieldValue('incentives', newIncentives);
    },
    [values.incentives]
  );

  const dataSource = values.incentives.map((e, i) => ({ ...e, index: i }));

  return (
    <Space direction="vertical" size="middle">
      <Title level={4}>{getTranslatedString('landlordPortal.incentives.incentives')}</Title>
      <p>{getTranslatedString('landlordPortal.incentives.incentivesDesc')}</p>
      {dataSource.length > 0 && (
        <Table
          rowKey={(record) => `${record.id}.${record.index}`}
          dataSource={dataSource}
          columns={[
            {
              title: getTranslatedString('landlordPortal.incentives.name'),
              dataType: 'text',
              dataIndex: 'name',
              key: 'name',
              width: '40%',
              ellipsis: true,
            },
            {
              title: getTranslatedString('landlordPortal.incentives.validFrom'),
              dataType: 'text',
              dataIndex: 'valid_from',
              key: 'valid_from',
              render: (_, rowData) => {
                return <span>{rowData.valid_from}</span>;
              },
            },
            {
              title: getTranslatedString('landlordPortal.incentives.validTo'),
              dataType: 'text',
              dataIndex: 'valid_to',
              key: 'valid_to',
              render: (_, rowData) => {
                return <span>{rowData.valid_to}</span>;
              },
            },
            {
              title: getTranslatedString('landlordPortal.incentives.units'),
              dataType: 'text',
              dataIndex: 'unit_ids',
              key: 'unit_ids',
              render: (_, rowData) => {
                return <span>{(rowData.unit_ids || []).length}</span>;
              },
            },
            {
              title: getTranslatedString('landlordPortal.incentives.actions'),
              dataIndex: 'actions',
              key: 'actions',
              render: (_, rowData) => {
                return (
                  <Space size="middle">
                    <Button
                      type="link"
                      icon={<EditTwoTone />}
                      onClick={() => handleEditClicked(rowData)}
                    />
                    <Button
                      type="link"
                      icon={<DeleteTwoTone />}
                      onClick={() => handleDeleteClicked(rowData)}
                    />
                  </Space>
                );
              },
            },
          ]}
          pagination={false}
        />
      )}
      <Button onClick={() => formModal.open()}>
        {getTranslatedString('landlordPortal.incentives.addIncentive')}
      </Button>
      <Form.Item
        validateStatus={touched.incentives && errors.incentives && 'error'}
        help={touched.incentives && errors.incentives}
        hasFeedback
        required
      />
      <IncentiveForm
        visible={formModal.isVisible}
        formData={selectedIndex === -1 ? {} : values.incentives[selectedIndex]}
        handleFormSubmit={handleFormSubmit}
        onClose={() => {
          formModal.close();
          setSelectedIndex(-1);
        }}
      />
    </Space>
  );
};

const Configuration = (props) => {
  const { values, errors, touched, setFieldValue, isMobile } = props;
  const startingMonthOptions = new Array(18).fill(null).map((_, i) => ({
    value: i + 1,
    label: getTranslatedString('landlordPortal.incentives.monthNOfLease', { n: i + 1 }),
  }));

  return (
    <div>
      <form id={props.formName} onSubmit={props.handleSubmit} className="ant-form-vertical">
        <FullWidthSpace direction="vertical" size="middle">
          <Title level={4}>
            {getTranslatedString('landlordPortal.incentives.incentiveTemplate')}
          </Title>
          <InternalName {...props} />
          <InternalDescription {...props} />
          <ExternallyVisible {...props} />
          {values.externally_visible && (
            <>
              <ExternalName {...props} />
              <ExternalDescription {...props} />
            </>
          )}
          <HalfColumnSpace size="middle" wrap>
            <Form.Item
              label={getTranslatedString('landlordPortal.incentives.incentiveType')}
              help={touched.incentive_type && errors.incentive_type}
              hasFeedback
              required
            >
              <Select
                optionData={[
                  {
                    value: 'rental_discount',
                    label: getTranslatedString('landlordPortal.incentives.rental_discount'),
                  },
                  {
                    value: 'fee_discount',
                    label: getTranslatedString('landlordPortal.incentives.fee_discount'),
                  },
                  {
                    value: 'fee_deferral',
                    label: getTranslatedString('landlordPortal.incentives.fee_deferral'),
                  },
                  { value: 'gift', label: getTranslatedString('landlordPortal.incentives.gift') },
                ]}
                value={values.incentive_type}
                error={errors.incentive_type && touched.incentive_type}
                handleChange={(value) => setFieldValue('incentive_type', value)}
                isMobile={isMobile}
              />
            </Form.Item>
            {['fee_discount', 'fee_deferral'].includes(values.incentive_type) && (
              <Form.Item
                label={getTranslatedString('landlordPortal.incentives.fee')}
                help={touched.target_fee && errors.target_fee}
                hasFeedback
                required
              >
                <Select
                  optionData={[
                    {
                      value: 'application_fee',
                      label: getTranslatedString('landlordPortal.incentives.applicationFee'),
                    },
                  ]}
                  value={values.target_fee}
                  error={errors.target_fee && touched.target_fee}
                  handleChange={(value) => setFieldValue('target_fee', value)}
                  isMobile={isMobile}
                />
              </Form.Item>
            )}
            {values.incentive_type === 'fee_deferral' && (
              <Form.Item
                label={getTranslatedString('landlordPortal.incentives.deferUntil')}
                help={touched.deferral_until_month && errors.deferral_until_month}
                hasFeedback
                required
              >
                <Select
                  optionData={startingMonthOptions}
                  value={values.deferral_until_month}
                  error={errors.deferral_until_month && touched.deferral_until_month}
                  handleChange={(value) => setFieldValue('deferral_until_month', value)}
                  isMobile={isMobile}
                />
              </Form.Item>
            )}
            <ApprovalType {...props} />
            <IncentiveCodes {...props} />
            <InternalCode {...props} />
          </HalfColumnSpace>
        </FullWidthSpace>
        <Divider />
        <FullWidthSpace direction="vertical" size="middle">
          <Title level={4}>{getTranslatedString('landlordPortal.incentives.schedule')}</Title>
          <ScheduleAndCalculationType {...props} />
          {props.values.schedule_type === 'monthly' && <MonthlyScheduleFields {...props} />}
        </FullWidthSpace>
      </form>
      {props.values.schedule_type === 'custom' && <CustomScheduleFields {...props} />}
      <Divider />
      <Incentives {...props} />
    </div>
  );
};

export default Configuration;
